// ============================================================================
// BALANCE.JS — Central tuning constants for Palm Island
// ============================================================================
// All game-designer-facing numbers live here. Import what you need:
//   import { CREATURE, INTERACTION } from './systems/balance.js';
// ============================================================================

// ---------------------------------------------------------------------------
// Creature behavior
// ---------------------------------------------------------------------------
export const CREATURE = {
  // Crab
  CRAB_COUNT: 8,
  CRAB_SPAWN_RADIUS_MIN: 15,
  CRAB_SPAWN_RADIUS_MAX: 30,
  CRAB_WANDER_RADIUS: 35,         // Turn back toward center beyond this
  CRAB_TELEPORT_RADIUS: 40,       // Hard teleport if beyond this
  CRAB_TELEPORT_RETURN_RADIUS: 30, // Teleport destination radius
  CRAB_PUSHBACK_RADIUS: 25,       // When off-sand, push back to this radius
  CRAB_BASE_SPEED: 2,
  CRAB_SCATTER_SPEED_MULTIPLIER: 3,
  CRAB_SCATTER_DURATION: 2.0,
  CRAB_SCATTER_SAND_INTERVAL: 4,   // Frames between sand puff checks (scatter)
  CRAB_NORMAL_SAND_INTERVAL: 15,   // Frames between sand puff checks (normal)
  CRAB_SCATTER_SAND_CHANCE: 1.0,
  CRAB_NORMAL_SAND_CHANCE: 0.3,
  CRAB_STAMPEDE_THRESHOLD: 3,      // Crabs scattering to trigger stampede

  // Dolphin
  DOLPHIN_COUNT: 5,
  DOLPHIN_ORBIT_RADIUS_MIN: 40,
  DOLPHIN_ORBIT_RADIUS_MAX: 60,
  DOLPHIN_ORBIT_SPEED_MIN: 0.2,
  DOLPHIN_ORBIT_SPEED_MAX: 0.3,
  DOLPHIN_BASE_Y: -2,
  DOLPHIN_JUMP_HEIGHT: 8,
  DOLPHIN_JUMP_MULTIPLIER_HIGH: 1.5,
  DOLPHIN_BACKFLIP_DURATION: 0.7,
  DOLPHIN_BARREL_ROLL_DURATION: 1.0,
  DOLPHIN_SPIN_DURATION: 1.0,
  DOLPHIN_HIGH_JUMP_DURATION: 2.0,
  DOLPHIN_SYNC_JUMP_DURATION: 2.5,
  DOLPHIN_SHOW_OFF_DURATION: 10.0,
  DOLPHIN_BARREL_ROLL_CLICK: 3,    // Click count to trigger barrel roll
  DOLPHIN_SYNC_CLICK_WINDOW: 2000, // ms window for synchronized splash

  // Seagull
  SEAGULL_COUNT: 12,
  SEAGULL_SPAWN_RADIUS_MIN: 30,
  SEAGULL_SPAWN_RADIUS_MAX: 50,
  SEAGULL_FLY_HEIGHT_MIN: 12,
  SEAGULL_FLY_HEIGHT_MAX: 35,
  SEAGULL_MAX_SPEED: 10,
  SEAGULL_ORBIT_INNER: 25,         // Push away if closer than this
  SEAGULL_ORBIT_OUTER: 50,         // Pull back if further than this
  SEAGULL_DIVE_DURATION: 1.5,
  SEAGULL_GOLDEN_CLICK_THRESHOLD: 10,
  SEAGULL_GOLDEN_SCALE: 1.15,
  SEAGULL_NORMAL_CATCH_CHANCE: 0.3,
  SEAGULL_GOLDEN_CATCH_CHANCE: 1.0,
  SEAGULL_BOID_NEIGHBOR_RADIUS: 10,

  // Formation flight
  FORMATION_FLIGHT_CLICK_WINDOW: 4000, // ms
  FORMATION_FLIGHT_UNIQUE_THRESHOLD: 3,
  FORMATION_FLIGHT_DURATION: 3.0,
  FORMATION_FLIGHT_SPEED: 15,
  FORMATION_FLIGHT_SPACING: 3.0,
  FORMATION_FLIGHT_ALTITUDE: 20,
};

// ---------------------------------------------------------------------------
// Interaction tuning
// ---------------------------------------------------------------------------
export const INTERACTION = {
  // Click cooldowns (ms)
  CREATURE_COOLDOWN: 500,
  OBJECT_COOLDOWN: 1000,
  MUSIC_NOTE_COOLDOWN: 100,

  // Bounding radii
  CRAB_BOUNDING_RADIUS: 1.5,
  DOLPHIN_BOUNDING_RADIUS: 3.0,
  SEAGULL_BOUNDING_RADIUS: 2.5,
  PALM_TREE_BOUNDING_RADIUS: 3.0,
  TREASURE_CHEST_BOUNDING_RADIUS: 2.5,
  WELCOME_SIGN_BOUNDING_RADIUS: 2.0,
  BEER_BOTTLE_BOUNDING_RADIUS: 0.8,
  BEACH_BUM_BOUNDING_RADIUS: 2.5,
  MUSIC_NOTE_BOUNDING_RADIUS: 0.8,
  SEASHELL_BOUNDING_RADIUS: 1.0,
  HERMIT_CRAB_BOUNDING_RADIUS: 0.6,
  TIDE_POOL_BOUNDING_RADIUS: 2.0,
  MESSAGE_BOTTLE_BOUNDING_RADIUS: 1.5,

  // Crab scatter / orchestra proximity
  CRAB_SCATTER_RADIUS: 8,          // Scatter all crabs within this distance
  CRAB_ORCHESTRA_RADIUS: 8,        // Crabs within this distance of beach bum
  CRAB_ORCHESTRA_MIN_CRABS: 2,     // Minimum crabs near bum to trigger
  CRAB_ORCHESTRA_WAVE_DURATION: 5.0,
  CRAB_WAVE_CLICK_INTERVAL: 5,     // Every Nth click triggers wave

  // Dolphin serenade timing
  DOLPHIN_SERENADE_WINDOW: 5000,   // ms after beach bum click

  // Cheers combo
  CHEERS_CLICK_WINDOW: 1500,       // ms between bottle clicks
  CHEERS_MIN_BOTTLES: 2,

  // Recycler combo
  RECYCLER_MIN_BOTTLES: 4,

  // Gesture cues
  GESTURE_IDLE_THRESHOLD: 15,      // Seconds of idle before triggering
  GESTURE_COOLDOWN: 30,            // Minimum seconds between gesture cues
  GESTURE_CUE_DURATION: 4.0,       // Duration of head gesture animation

  // Hum proximity
  HUM_PROXIMITY_RADIUS: 12,
  HUM_COOLDOWN_MS: 20000,
  HUM_CHECK_INTERVAL: 60,          // Frames between proximity checks
  HUM_VOLUME_MIN: 0.03,
  HUM_VOLUME_MAX: 0.15,

  // Music note magnetism
  MUSIC_NOTE_MAGNET_RADIUS_TOUCH: 5.0,
  MUSIC_NOTE_MAGNET_RADIUS_MOUSE: 3.0,
  MUSIC_NOTE_MAGNET_SPEED: 0.5,

  // Music note catching milestones
  MELODY_MAKER_THRESHOLD: 10,      // Notes caught to trigger melody maker

  // Secret visibility
  SECRET_CAMERA_DISTANCE: 15,      // Max distance to see seashell / hermit crab

  // Player idle speech bubble
  IDLE_SPEECH_THRESHOLD: 30,       // Seconds before "try clicking" bubble

  // Drag detection thresholds (px)
  DRAG_THRESHOLD_TOUCH: 15,
  DRAG_THRESHOLD_MOUSE: 5,
};

// ---------------------------------------------------------------------------
// Particle effects
// ---------------------------------------------------------------------------
export const PARTICLES = {
  // Pool sizes
  MAX_SPLASH_PARTICLES: 150,
  MAX_SAND_PARTICLES: 100,
  MAX_SPARKLE_PARTICLES: 80,
  MAX_MUSIC_PARTICLES: 30,

  // Sparkle counts per event
  SECRET_SPARKLE_COUNT: { min: 12, max: 15 },  // secrets & combos
  STANDARD_SPARKLE_COUNT: 5,                     // normal discoveries
  TREASURE_SPARKLE_COUNT: 10,
  SECRET_OBJECT_SPARKLE_COUNT: 20,               // seashell / tide pool / bottle

  // Music note sparkle on catch
  NOTE_CATCH_SPARKLE_COUNT: 5,
};

// ---------------------------------------------------------------------------
// Discovery system
// ---------------------------------------------------------------------------
export const DISCOVERY = {
  TOAST_DURATION_STANDARD: 3000,   // ms
  TOAST_DURATION_HIGH_TIER: 5000,  // ms (secrets & combos)
  TOAST_GAP: 300,                  // ms between consecutive toasts
  TOAST_PROGRESS_THRESHOLD: 5,     // Show progress counter after this many

  // Milestone thresholds
  MILESTONES: [
    { count: 5, phrase: "You're getting the hang of it!" },
    { count: 10, phrase: "Halfway there, explorer!" },
    { count: 15, phrase: "Almost found everything!" },
    { count: 20, phrase: "What a legend!" },
  ],

  // Speech bubble durations for milestone / creature / combo events
  CREATURE_BUBBLE_DURATION: 4000,
  COMBO_BUBBLE_DURATION: 5000,
  ALL_SECRETS_BUBBLE_DURATION: 5000,
  MILESTONE_BUBBLE_DURATION: 4000,
  MILESTONE_BUBBLE_DELAY: 500,     // ms delay before showing bubble
};

// ---------------------------------------------------------------------------
// Beach bum tuning
// ---------------------------------------------------------------------------
export const BEACH_BUM = {
  // Strum speed modifiers
  CLICK_STRUM_MULTIPLIER: 3.0,
  CLICK_NOTE_MULTIPLIER: 5.0,
  GUITAR_SOLO_STRUM_MULTIPLIER: 5.0,
  GUITAR_SOLO_NOTE_MULTIPLIER: 10.0,

  // Timers
  SPEED_UP_DURATION: 2.0,          // seconds
  HEAD_TILT_DURATION: 2.0,
  GUITAR_SOLO_DURATION: 8.0,
  GUITAR_SOLO_BURST_INTERVAL: 0.5,
  GUITAR_SOLO_CLICK_THRESHOLD: 5,

  // Music notes
  NOTE_SPAWN_CHANCE: 0.025,        // Per-frame probability
  NOTE_MAX_LIFE_MIN: 8.0,
  NOTE_MAX_LIFE_MAX: 10.0,
  NOTE_BURST_COUNT: 5,

  // Idle behavior
  IDLE_STATE_DURATION_MIN: 15,
  IDLE_STATE_DURATION_MAX: 30,

  // Reaction durations
  THUMBS_UP_DURATION: 1.5,
  APPLAUSE_DURATION: 2.0,
};

// ---------------------------------------------------------------------------
// Animation timings
// ---------------------------------------------------------------------------
export const ANIMATION = {
  // Crab wave
  CRAB_WAVE_DURATION: 1.1,
  CRAB_WAVE_RAISE_TIME: 0.3,
  CRAB_WAVE_LOWER_TIME: 0.3,

  // Treasure chest wobble
  CHEST_WOBBLE_DURATION: 0.5,
  CHEST_WOBBLE_CYCLES: 3,

  // Welcome sign wobble
  SIGN_WOBBLE_DURATION: 0.8,
  SIGN_WOBBLE_CYCLES: 2,

  // Bottle flash restore time
  BOTTLE_FLASH_DURATION: 0.3,
  BOTTLE_ROLL_DURATION: 0.8,
  BOTTLE_RESET_DURATION: 120,      // seconds

  // Floating gem / shard rise + fade
  FLOAT_RISE_DURATION: 1.5,
  FLOAT_RISE_HEIGHT: 3.0,

  // Coconut removal timer
  COCONUT_REST_SECONDS: 30,
  COCONUT_REFILL_MS: 180000,       // 3 minutes

  // Chest daily reset
  CHEST_DAILY_RESET_MS: 86400000,  // 24 hours
};

// ---------------------------------------------------------------------------
// Phase 2 placeholder constants
// ---------------------------------------------------------------------------
export const RESOURCES = {
  DROP_RATES: {
    coconut_per_tree: 3,
    gem_per_click: 1,
    fish_dive_chance: 0.3,
  },
  STACK_LIMITS: {
    coconut: 50,
    coconut_shell: 99,
    gem: 99,
    gold_coin: 99,
    seashell: 99,
    glass_shard: 99,
    driftwood: 50,
    fish: 30,
    melody_fragment: 20,
    sand_dollar: 50,
  },
  SPAWN_MIN_SPACING: 3,
};

export const PLACEMENT = {
  MIN_SPACING: 2,
  BASE_OBJECT_LIMIT: 10,
};

export const EXPANSION = {
  BASE_RADIUS: 60,
  MULTIPLIER: 1.2,              // 1.2x terrain radius when expanded
  EXPANDED_RADIUS: 72,          // 60 * 1.2
  EXPANDED_SIZE: 144,           // radius * 2
  NEW_PALM_TREE_RING_RADIUS: 50, // Ring for new trees in expanded zone
  NEW_PALM_TREE_COUNT: 3,       // Number of new trees to add
};
